import { useEffect, useRef, useState } from 'react'
import { ImagePlus, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

// Mirrors the hidden input's accept list; dropped files are filtered to the
// same set so drag & drop behaves like the file picker.
const ACCEPTED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp'])

export interface PendingBoatPhoto {
  id: string
  file: File
  previewUrl: string
  /**
   * Downscale/EXIF-strip kicked off the moment the photo is picked, so the
   * canvas work overlaps form-fill instead of serializing behind submit.
   * Awaited at upload time; never rejects (falls back to the original file).
   */
  processed: Promise<File>
}

interface BoatPhotoPickerProps {
  photos: PendingBoatPhoto[]
  onAdd: (files: File[]) => void
  onRemove: (id: string) => void
}

// The create dialog doesn't scroll, so the grid is capped at two rows: the
// first 7 photos render as thumbnails and the 8th slot is the Add button,
// which doubles as the "+ N more" indicator for hidden photos.
const MAX_THUMBNAILS = 7

// Picks photos while a boat is being created; they upload after the boat
// itself is saved. Fully controlled — the parent owns the list and the
// object-URL lifetimes. The whole section is a drop target, so multiple
// files can be dragged in whether or not photos were added already.
export function BoatPhotoPicker({ photos, onAdd, onRemove }: BoatPhotoPickerProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  const addFiles = (files: FileList | null) => {
    if (!files?.length) return
    onAdd(Array.from(files).filter((f) => ACCEPTED_IMAGE_TYPES.has(f.type)))
  }

  // A drop that misses the section would otherwise make the browser navigate
  // to the dropped file, losing the whole in-progress form.
  useEffect(() => {
    const prevent = (e: DragEvent) => e.preventDefault()
    document.addEventListener('dragover', prevent)
    document.addEventListener('drop', prevent)
    return () => {
      document.removeEventListener('dragover', prevent)
      document.removeEventListener('drop', prevent)
    }
  }, [])

  return (
    <div
      className="relative space-y-2"
      onDragOver={(e) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'copy'
      }}
      onDragEnter={(e) => {
        if (e.dataTransfer.types.includes('Files')) setIsDragging(true)
      }}
      onDragLeave={(e) => {
        // Child elements fire dragleave on the way to the parent; only
        // clear the highlight once the pointer actually left the section.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setIsDragging(false)
      }}
      onDrop={(e) => {
        e.preventDefault()
        setIsDragging(false)
        addFiles(e.dataTransfer.files)
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        className="hidden"
        onChange={(e) => {
          addFiles(e.target.files)
          if (inputRef.current) inputRef.current.value = ''
        }}
      />
      {photos.length === 0 ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex h-24 w-full flex-col items-center justify-center gap-1 rounded-md border border-dashed text-muted-foreground transition-colors hover:border-primary hover:text-primary"
        >
          <ImagePlus className="h-6 w-6" />
          <span className="text-sm">Add photos (optional)</span>
          <span className="text-xs">Drag &amp; drop or click to browse</span>
        </button>
      ) : (
        <div className="grid grid-cols-4 gap-2">
          {photos.slice(0, MAX_THUMBNAILS).map((photo) => (
            <div key={photo.id} className="group relative overflow-hidden rounded-md border">
              <img
                src={photo.previewUrl}
                alt={photo.file.name}
                className="aspect-square w-full object-cover"
              />
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="absolute right-1 top-1 h-6 w-6 opacity-0 transition-opacity group-hover:opacity-100"
                title="Remove photo"
                onClick={() => onRemove(photo.id)}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex aspect-square w-full flex-col items-center justify-center gap-0.5 rounded-md border border-dashed text-muted-foreground transition-colors hover:border-primary hover:text-primary"
          >
            <ImagePlus className="h-5 w-5" />
            <span className="text-[10px]">Add</span>
            {photos.length > MAX_THUMBNAILS && (
              <span className="text-[10px] font-medium">
                + {photos.length - MAX_THUMBNAILS} more
              </span>
            )}
          </button>
        </div>
      )}
      {isDragging && (
        <div
          className={cn(
            'pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-md border-2 border-dashed border-primary bg-background/80',
            photos.length > 0 && 'backdrop-blur-[1px]',
          )}
        >
          <span className="text-sm font-medium text-primary">Drop photos to add</span>
        </div>
      )}
    </div>
  )
}
